import React, { useCallback, useEffect, useState } from "react";
import DataProvider from "../../apis";
import CustomInput from "../form/custom-input";
import PasswordInput from "../form/password-input";
import EmailInput from "../form/email-input";
import { Button, message } from "antd";
import LoadWrapper from "../elements/load-wrapper";
import UploadPicture from "./upload-picture";
import { useAppStore } from "../../store";
import { UserOutlined, IdcardOutlined } from "@ant-design/icons";

export default function AccountInfo(props: { tab?: string }) {
  const { setUser } = useAppStore();
  const { tab } = props;
  const [messageApi, contextHolder] = message.useMessage();
  const [innerTab, setInnerTab] = useState<number | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [image, setImage] = useState<string | null>(null);
  const [bio, setBio] = useState("");
  const [speciality, setSpeciality] = useState("");
  const [license, setLicense] = useState("");
  const [institution, setInstitution] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingx, setLoadingx] = useState(false);

  const getProfile = useCallback(async () => {
    setRefreshing(true);
    const { data } = await DataProvider.getProfile();
    setUser({ ...data });
    setEmail(data.email ?? "");
    setName(data.fullname ?? "");
    setPhone(data.phoneNumber ?? "");
    setImage(data.picture ?? null);

    if (data.doctor) {
      setBio(data.doctor.bio ?? "");
      setSpeciality(data.doctor.speciality ?? "");
      setLicense(data.doctor.licenseId ?? "");
      setInstitution(data.doctor.institution ?? "");
    }
    setRefreshing(false);
  }, [setUser]);

  useEffect(() => {
    if (tab === "1" && innerTab === null) {
      getProfile();
    }
  }, [tab, getProfile, innerTab]);

  const updateProfile = async () => {
    setLoading(true);
    messageApi
      .open({
        type: "loading",
        content: "Updating profile…",
        duration: 2.5,
      })
      .then(async () => {
        try {
          const payload: {
            email?: string;
            fullname?: string;
            phoneNumber?: string;
          } = {
            fullname: name,
            phoneNumber: phone,
          };
          if (email) payload.email = email;
          const { data } = await DataProvider.updateProfile(payload);
          setUser({ ...data });
          setLoading(false);
          message.success("Profile updated successfully.");
          getProfile();
        } catch {
          setLoading(false);
          message.error("Failed to update profile. Try again later.");
        }
      });
  };

  const updateDoctor = async () => {
    setLoadingx(true);
    messageApi
      .open({
        type: "loading",
        content: "Updating doctor profile…",
        duration: 2.5,
      })
      .then(async () => {
        try {
          await DataProvider.updateMyDoctorProfile({
            licenseId: license,
            institution,
            speciality,
            bio,
          });
          setLoadingx(false);
          message.success("Doctor profile updated successfully.");
          getProfile();
        } catch {
          setLoadingx(false);
          message.error("Failed to update profile. Try again later.");
        }
      });
  };

  if (innerTab === 0) {
    return (
      <div
        className="min-h-[70vh] w-full rounded-2xl p-4 md:p-6"
        style={{
          background:
            "linear-gradient(160deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)",
        }}
      >
        <UploadPicture innerTab={innerTab} setInnerTab={setInnerTab} />
        {contextHolder}
      </div>
    );
  }

  return (
    <div
      className="min-h-[70vh] w-full rounded-2xl p-4 md:p-6"
      style={{
        background:
          "linear-gradient(160deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)",
      }}
    >
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-2">
        {/* Personal information */}
        <section
          className="flex flex-col rounded-2xl border border-slate-200/80 bg-white/95 p-6 shadow-lg shadow-slate-200/50 backdrop-blur-sm md:p-8"
          aria-labelledby="personal-heading"
        >
          <div className="mb-6 flex items-center gap-3">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white shadow-md"
              style={{
                background: "linear-gradient(135deg, #00A2F8 0%, #0070C0 100%)",
              }}
              aria-hidden
            >
              <UserOutlined className="text-xl" />
            </div>
            <h2
              id="personal-heading"
              className="text-xl font-semibold tracking-tight text-slate-800"
            >
              Personal information
            </h2>
          </div>

          <LoadWrapper loading={refreshing}>
            <div className="space-y-5">
              <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                <div className="shrink-0">
                  <p className="mb-1 text-sm font-medium text-slate-700">
                    Your photo
                  </p>
                  <p className="text-xs text-slate-500">
                    Shown on your profile.
                  </p>
                </div>
                <div className="flex flex-col items-center gap-3 sm:ml-4">
                  <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border-2 border-slate-200 bg-slate-50 shadow-inner">
                    {image ? (
                      <img
                        src={image}
                        alt="Profile"
                        className="h-24 w-24 rounded-full object-cover"
                      />
                    ) : (
                      <UserOutlined className="text-4xl text-slate-400" />
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="link"
                      className="text-slate-500 hover:text-slate-700"
                      aria-label="Delete photo"
                    >
                      Delete
                    </Button>
                    <Button
                      type="primary"
                      size="small"
                      className="rounded-xl bg-[#00A2F8] font-medium hover:bg-[#0070C0]"
                      onClick={() => setInnerTab(0)}
                      aria-label="Update photo"
                    >
                      Update
                    </Button>
                  </div>
                </div>
              </div>

              <CustomInput
                title="Name"
                placeholder="Enter your full name"
                value={name}
                onChangeText={setName}
              />
              <EmailInput
                noTitle
                email={email}
                setEmail={setEmail}
                placeholder="Enter your email address"
              />
              <CustomInput
                title="Contact"
                placeholder="Enter your phone number"
                value={phone}
                onChangeText={setPhone}
              />
              <PasswordInput
                required
                disable={true}
                noTitle
                change
                password={password}
                setPassword={setPassword}
                placeholder="••••••••••••"
              />

              <div className="pt-2">
                <Button
                  type="primary"
                  size="large"
                  onClick={updateProfile}
                  loading={loading}
                  className="h-12 rounded-xl bg-[#00A2F8] px-8 font-semibold shadow-md transition-all hover:bg-[#0070C0] hover:shadow-lg"
                  aria-label={
                    loading ? "Updating profile" : "Update personal information"
                  }
                >
                  {loading ? "Updating…" : "Update information"}
                </Button>
              </div>
            </div>
          </LoadWrapper>
        </section>

        {/* Doctor information */}
        <section
          className="flex flex-col rounded-2xl border border-slate-200/80 bg-white/95 p-6 shadow-lg shadow-slate-200/50 backdrop-blur-sm md:p-8"
          aria-labelledby="doctor-heading"
        >
          <div className="mb-6 flex items-center gap-3">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white shadow-md"
              style={{
                background: "linear-gradient(135deg, #00A2F8 0%, #0070C0 100%)",
              }}
              aria-hidden
            >
              <IdcardOutlined className="text-xl" />
            </div>
            <h2
              id="doctor-heading"
              className="text-xl font-semibold tracking-tight text-slate-800"
            >
              Doctor information
            </h2>
          </div>

          <LoadWrapper loading={refreshing}>
            <div className="space-y-5">
              <CustomInput
                title="License ID"
                placeholder="Enter your license ID"
                value={license}
                onChangeText={setLicense}
              />
              <CustomInput
                title="Institution"
                placeholder="Enter your place of work"
                value={institution}
                onChangeText={setInstitution}
              />
              <CustomInput
                title="Speciality"
                placeholder="e.g. General Practice, Cardiology"
                value={speciality}
                onChangeText={setSpeciality}
              />
              <div>
                <label
                  htmlFor="account-bio"
                  className="mb-1.5 block text-sm font-medium text-slate-700"
                >
                  Bio
                </label>
                <textarea
                  id="account-bio"
                  name="bio"
                  rows={4}
                  placeholder="A brief about what you do…"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-800 outline-none transition-all duration-200 focus:border-[#00A2F8] focus:ring-2 focus:ring-[#00A2F8]/20 resize-y min-h-[100px]"
                  aria-describedby="account-bio-hint"
                />
                <p
                  id="account-bio-hint"
                  className="mt-1 text-xs text-slate-500"
                >
                  This is shown on your public profile.
                </p>
              </div>

              <div className="pt-2">
                <Button
                  type="primary"
                  size="large"
                  onClick={updateDoctor}
                  loading={loadingx}
                  className="h-12 rounded-xl bg-[#00A2F8] px-8 font-semibold shadow-md transition-all hover:bg-[#0070C0] hover:shadow-lg"
                  aria-label={
                    loadingx
                      ? "Updating doctor profile"
                      : "Update doctor information"
                  }
                >
                  {loadingx ? "Updating…" : "Update doctor information"}
                </Button>
              </div>
            </div>
          </LoadWrapper>
        </section>
      </div>
      {contextHolder}
    </div>
  );
}
